import * as React from 'react';
import type { TableData } from '../types';

/**
 * Recorded values in columns. Client-side sort is the only interaction — the widget never
 * aggregates, totals or derives, because a derived number with no producer is an assertion.
 * Null cells render an explicit em-dash with a "not recorded" tooltip, not an empty hole a
 * reader could mistake for zero-rows-matched.
 */
export function Table({ table }: { table: TableData }) {
  const [sort, setSort] = React.useState<{ col: number; dir: 1 | -1 } | null>(null);

  const rows = React.useMemo(() => {
    if (!sort) {
      return table.rows;
    }
    return [...table.rows].sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      // Nulls always sort last regardless of direction: "not recorded" is not a small value.
      if (av === null || av === undefined) {
        return 1;
      }
      if (bv === null || bv === undefined) {
        return -1;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * sort.dir;
      }
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  }, [table.rows, sort]);

  return (
    <table className="docit-widget-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr>
          {table.columns.map((c, i) => (
            <th
              key={c + i}
              onClick={() =>
                setSort((s) => (s?.col === i ? { col: i, dir: s.dir === 1 ? -1 : 1 } : { col: i, dir: 1 }))
              }
              style={{
                // `start`, not `left`: this package is deliberately
                // styled-components-free, so nothing mirrors it automatically and
                // these tables render inside user documents.
                textAlign: 'start',
                cursor: 'pointer',
                padding: '4px 8px',
                borderBottom: '1px solid currentColor',
                opacity: 0.8,
                whiteSpace: 'nowrap',
              }}
              title="Sort"
            >
              {c}
              {sort?.col === i ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td key={ci} style={{ padding: '4px 8px', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
                {cell === null || cell === undefined ? (
                  <span title="not recorded" style={{ opacity: 0.45 }}>—</span>
                ) : (
                  String(cell)
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
